import React from "react";
import {IonButton} from "@ionic/react";
import {Booking, Company, ProfilePackage} from "../../models";
import {Calendar} from "@awesome-cordova-plugins/calendar";
import {tsToServerDate} from "../../utils/ages";
import {useToastStore} from "../../redux/toast";

interface BookingAddToCalendarButtonProps {
    company: Company;
    booking: Booking;
    profilePackage: ProfilePackage;
    actionsDisabled?: boolean;

    calendarText?: string;
    calendarEnabled?: boolean;

    expand?: 'full' | 'block';
    fill?: 'clear' | 'outline' | 'solid' | 'default';
}

const BookingAddToCalendarButton: React.FC<BookingAddToCalendarButtonProps> = ({
                                                     company,
                                                     booking,
                                                     profilePackage,
                                                     actionsDisabled = false,
                                                     calendarText = "Add to Calendar",
                                                     calendarEnabled = true,
                                                     expand,
                                                     fill,
                                                 }) => {
    const toastStore = useToastStore();
    const isRecurring = profilePackage?.isRecurring;

    const handleCalendarClicked = (booking: Booking) => {
        // The server's display fields carry the school's wall-clock schedule
        // ("15:00"); build the event from them directly rather than converting
        // instants, so every client timezone writes the same calendar entry.
        const parseTimeDisplay = (display: string): [number, number] => {
            const [hours, minutes] = display.split(":").map(Number);
            return [hours, minutes];
        };

        const createEvent = () => {
            if (!booking || !booking.slot){return;}
            const [startHours, startMinutes] = parseTimeDisplay(booking.slot.startTimeDisplay);
            const [endHours, endMinutes] = parseTimeDisplay(booking.slot.endTimeDisplay);
            const startedDate = tsToServerDate(booking.startedTimestamp);
            const bookingStart = new Date(startedDate.getFullYear(),
                startedDate.getMonth(),
                startedDate.getDate(),
                startHours,
                startMinutes
            );
            // The event always ends the same day it starts; for recurring
            // bookings the *recurrence* runs until the package expiry date.
            const bookingEnd = new Date(startedDate.getFullYear(),
                startedDate.getMonth(),
                startedDate.getDate(),
                endHours,
                endMinutes
            );
            const expiredDate = isRecurring && booking.expiredTimestamp ? tsToServerDate(booking.expiredTimestamp) : undefined;
            const recurrenceEndDate = expiredDate ? new Date(expiredDate.getFullYear(),
                expiredDate.getMonth(),
                expiredDate.getDate(),
                23, 59, 59
            ) : undefined;

            Calendar.createEventInteractivelyWithOptions(
                `${booking.slot.lessonType ? booking.slot.lessonType?.name : ''} ${company?.langLesson}`,
                `${booking.slot.facility?.name}`,
                `With ${booking.slot.employees?.map((employee) => employee.fullName).join(", ")}`,
                bookingStart,
                bookingEnd,
                {
                    id: `swool-booking-${booking.id}-${booking.startedTimestamp}`,
                    ...(isRecurring ? {
                        recurrence: "weekly",
                        // The plugin's interval unit is weeks: repeat every week.
                        recurrenceInterval: 1,
                        ...(recurrenceEndDate ? {recurrenceEndDate} : {}),
                    } : {})
                }
            ).catch((reason) => {
                toastStore.showError({
                    header: "Calendar event could not be created",
                    message: reason,
                });
            })
        };

        Calendar.requestWritePermission()
            .then(() => {
                createEvent();
            }).catch((reason) => {
                toastStore.showError({
                    header: "Calendar is not accessible or permission is denied",
                    // message: reason,
                })
            }
        );
    }

    const calendarButton = calendarEnabled && (
        <IonButton
            key={`booking-${booking.id}-calendar`}
            disabled={actionsDisabled}
            color={"tertiary"}
            expand={expand}
            fill={fill}
            onClick={() => handleCalendarClicked(booking)}
        >
            {calendarText}
        </IonButton>
    );

    if (!profilePackage || (!calendarButton)){
        return null;
    }
    return (
        <>
            {calendarButton}
        </>
    );
};

export default BookingAddToCalendarButton;
